import React from 'react';
import { 
  Wifi, 
  Box, 
  Dumbbell, 
  TrendingUp, 
  TrendingDown,
  ExternalLink,
  FileText,
} from 'lucide-react';
import { useEmpresasResumoCards } from 'hooks/useEmpresasResumoCards';

const EmpresasResumoCards = () => {
  const {
    dadosEmpresas, 
    loading, 
    error, 
    progresso,
    recarregar
  } = useEmpresasResumoCards();

  // Mapear ícones baseado no nome
  const getIcone = (iconeName) => {
    const icones = {
      'Wifi': Wifi,
      'Box': Box,
      'Dumbbell': Dumbbell
    };
    return icones[iconeName] || Wifi;
  };

  const getCompanyColor = (empresa) => {
    if (empresa?.primary_color) return empresa.primary_color;
    const nome = empresa?.nome || empresa?.name || '';
    const cores = {
      'MOV Fibra': '#8B5CF6',
      'XT Fibra': '#DC2626',
      'MOV Fit': '#7C3AED'
    };
    return cores[nome] || '#6B7280';
  };

  const withAlpha = (hex, alpha = '22') => {
    if (!hex || typeof hex !== 'string') return undefined;
    if (hex.length === 7) return `${hex}${alpha}`;
    return hex;
  };

  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(valor);
  };

  // Estado de loading com informações de progresso
  if (loading) {
    return (
      <div className="mb-6">
        <div className="mb-3">
          <div className="text-xs font-semibold tracking-[0.2em] text-[color:var(--text-muted)] uppercase">
            Performance
          </div>
          <p className="text-xs text-[color:var(--text-muted)]">
            Carregando dados...
          </p>
        </div>
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              className="rounded-lg p-4 border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_2px_10px_rgba(15,23,42,0.08)] animate-pulse"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[color:var(--surface-muted)] rounded-lg" />
                <div className="flex-1">
                  <div className="h-3 w-24 bg-[color:var(--surface-muted)] rounded mb-2"></div>
                  <div className="h-3 w-16 bg-[color:var(--surface-muted)] rounded"></div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-3 w-full bg-[color:var(--surface-muted)] rounded"></div>
                <div className="h-3 w-full bg-[color:var(--surface-muted)] rounded"></div>
                <div className="h-3 w-full bg-[color:var(--surface-muted)] rounded"></div>
                <div className="h-3 w-full bg-[color:var(--surface-muted)] rounded"></div>
                <div className="h-2 w-full bg-[color:var(--surface-muted)] rounded"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="md:hidden space-y-3">
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              className="rounded-lg p-4 border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_2px_10px_rgba(15,23,42,0.08)] animate-pulse"
            >
              <div className="h-3 w-24 bg-[color:var(--surface-muted)] rounded mb-2"></div>
              <div className="h-3 w-full bg-[color:var(--surface-muted)] rounded mb-2"></div>
              <div className="h-3 w-full bg-[color:var(--surface-muted)] rounded"></div>
            </div>
          ))}
        </div>
        {progresso && (
          <div className="mt-4 text-center">
            <div className="text-sm text-[color:var(--text-muted)] mb-2">
              {progresso.carregadas} de {progresso.total} empresas carregadas
            </div>
            <div className="w-48 bg-[color:var(--surface-muted)] rounded-full h-2 mx-auto">
              <div
                className="bg-sky-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progresso.percentual}%` }}
              ></div>
            </div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1">
              {progresso.percentual}%
            </div>
          </div>
        )}
      </div>
    );
  }

  // Estado de erro
  if (error) {
    return (
      <div className="mb-6">
        <div className="mb-3">
          <div className="text-xs font-semibold tracking-[0.2em] text-[color:var(--text-muted)] uppercase">
            Performance
          </div>
          <p className="text-xs text-rose-300">
            Erro ao carregar dados: {error}
          </p>
        </div>
        <div className="flex items-center justify-center py-8">
          <button
            onClick={recarregar}
            className="px-4 py-2 bg-sky-500/80 text-white rounded-lg hover:bg-sky-500 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="mb-6">
      {/* Título da seção */}
      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs font-semibold tracking-[0.2em] text-[color:var(--text-muted)] uppercase">
          Performance
        </div>
        <button
          onClick={recarregar}
          className="px-3 py-1 text-xs font-semibold text-sky-600 hover:text-sky-700 transition-colors focus-visible:ring-1 focus-visible:ring-sky-300/40 rounded-md"
          title="Atualizar dados"
        >
          Atualizar
        </button>
      </div>


      {/* Cards das empresas */}
      {dadosEmpresas.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-[color:var(--text-muted)]">Nenhuma empresa encontrada</p>
        </div>
      ) : (
        <div className="relative">
          {/* Desktop: Grid tradicional */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-4">
            {dadosEmpresas.map((empresa) => {
              const Icone = getIcone(empresa.icone);
              const corEmpresa = getCompanyColor(empresa);
            
              return (
                <div 
                  key={empresa.id}
                  className="rounded-lg p-4 border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_6px_18px_rgba(15,23,42,0.08)] hover:shadow-[0_10px_24px_rgba(15,23,42,0.12)] hover:bg-[color:var(--surface-muted)] transition-all duration-200"
                >
                  {/* Header do card */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center ring-1 ring-[color:var(--border)]"
                        style={{ backgroundColor: withAlpha(corEmpresa) }}
                      >
                        <Icone className="h-5 w-5" style={{ color: corEmpresa }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[color:var(--text-primary)] text-sm">
                          {empresa.nome}
                        </h3>
                        {empresa.status && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]">
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                empresa.success
                                  ? 'bg-emerald-500'
                                  : empresa.status === 'SEM DADOS'
                                  ? 'bg-amber-500'
                                  : 'bg-rose-500'
                              }`}
                            />
                            {empresa.status}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Ações */}
                    <div className="flex items-center space-x-1">
                      <button className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors">
                        <FileText className="h-4 w-4" />
                      </button>
                      <button className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors">
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Métricas financeiras */}
                  <div className="space-y-3">
                    {/* Receitas Brutas */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[color:var(--text-primary)]">Receitas Brutas</span>
                      <span className="text-sm font-medium text-[color:var(--text-primary)]">
                        {formatarValor(empresa.receitasBrutas || empresa.receitas)}
                      </span>
                    </div>

                    {/* Receitas Líquidas */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[color:var(--text-primary)]">Receitas Líquidas</span>
                      <span className="text-sm font-medium text-[color:var(--text-primary)]">
                        {formatarValor(empresa.receitas)}
                      </span>
                    </div>

                    {/* Despesas */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[color:var(--text-primary)]">Despesas</span>
                      <span className="text-sm font-medium text-[color:var(--text-primary)]">
                        {formatarValor(empresa.despesas)}
                      </span>
                    </div>

                    {/* Resultado */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[color:var(--text-primary)]">Resultado</span>
                      <span className="text-sm font-medium text-[color:var(--text-primary)]">
                        {formatarValor(empresa.resultado)}
                      </span>
                    </div>

                    {/* Barra de progresso do resultado */}
                    <div className="w-full bg-[color:var(--surface-muted)] rounded-full h-2">
                      <div 
                        className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.max(0, Math.min(100, empresa.percentual))}%` }}
                      ></div>
                    </div>

                    {/* Percentual e tendência */}
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-semibold text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] px-2 py-1 rounded-full">
                        {empresa.percentual.toFixed(1)}%
                      </span>
                      
                      <div className={`flex items-center space-x-1 text-xs font-semibold ${
                        empresa.tendenciaPositiva ? 'text-emerald-600' : 'text-rose-600'
                      }`}>
                        {empresa.tendenciaPositiva ? (
                          <TrendingUp className="h-3 w-3" />
                        ) : (
                          <TrendingDown className="h-3 w-3" />
                        )}
                        <span>{empresa.tendencia}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile: Lista vertical */}
          <div className="md:hidden space-y-3">
              {dadosEmpresas.map((empresa) => {
                const Icone = getIcone(empresa.icone);
                const corEmpresa = getCompanyColor(empresa);

                return (
                <div
                  key={empresa.id}
                  className="w-full"
                >
                  <div className="rounded-lg p-3 border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_4px_12px_rgba(15,23,42,0.08)]">
                    {/* Header do card */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center ring-1 ring-[color:var(--border)]"
                            style={{ backgroundColor: withAlpha(corEmpresa) }}
                          >
                            <Icone className="h-5 w-5" style={{ color: corEmpresa }} />
                          </div>
                        <div>
                          <h3 className="font-semibold text-[color:var(--text-primary)] text-sm">
                            {empresa.nome}
                          </h3>
                          {empresa.status && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]">
                              <span
                                className={`h-1.5 w-1.5 rounded-full ${
                                  empresa.success
                                    ? 'bg-emerald-500'
                                    : empresa.status === 'SEM DADOS'
                                    ? 'bg-amber-500'
                                    : 'bg-rose-500'
                                }`}
                              />
                              {empresa.status}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Metricas financeiras */}
                    <div className="space-y-2.5">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[color:var(--text-primary)]">Receitas Brutas</span>
                        <span className="text-sm font-medium text-[color:var(--text-primary)]">
                          {formatarValor(empresa.receitasBrutas || empresa.receitas)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[color:var(--text-primary)]">Receitas Líquidas</span>
                        <span className="text-sm font-medium text-[color:var(--text-primary)]">
                          {formatarValor(empresa.receitas)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[color:var(--text-primary)]">Despesas</span>
                        <span className="text-sm font-medium text-[color:var(--text-primary)]">
                          {formatarValor(empresa.despesas)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[color:var(--text-primary)]">Resultado</span>
                        <span className="text-sm font-medium text-[color:var(--text-primary)]">
                          {formatarValor(empresa.resultado)}
                        </span>
                      </div>
                      <div className="w-full bg-[color:var(--surface-muted)] rounded-full h-2">
                        <div
                          className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.max(0, Math.min(100, empresa.percentual))}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] px-2 py-1 rounded-full">
                          {empresa.percentual.toFixed(1)}%
                        </span>
                        <div className={`flex items-center space-x-1 text-xs font-semibold ${
                          empresa.tendenciaPositiva ? 'text-emerald-600' : 'text-rose-600'
                        }`}>
                          {empresa.tendenciaPositiva ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (
                            <TrendingDown className="h-3 w-3" />
                          )}
                          <span>{empresa.tendencia}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


export default EmpresasResumoCards;
